// src/lib/cravingResults.js
//
// Ticket 80 -- record what a craving returned, and what the reader did with it.
//
//   recordCravingResults  the first page of a craving's first run: the books in
//                         the order match_books gave them, with each one's
//                         similarity, and whether a filter was on for that page
//                         (a filter applied to one craving stays on for the
//                         next, so a craving's first run can be filtered).
//                         Written once per craving, by a database function
//                         (record_craving_results) that refuses a second write
//                         and a craving saved more than an hour ago. The page
//                         also writes cravings.best_match_similarity, from the
//                         same place.
//   recordCravingAction   a Save, Buy (a retailer link) or Already Read from the
//                         deck, tied to the craving that showed the book
//                         (record_craving_action). An event row, not a library
//                         column: Buy writes nothing else anywhere.
//
// Both are bookkeeping for the admin counts, never something the reader waits
// on, so neither throws. Each returns { ok, status, error } and the caller
// decides what is worth reporting.
//
// What is recorded: craving id, book id, position, similarity, action. Never a
// reader: neither table has a column for one.

import { supabase } from './supabase';
import { similarityFromDistance } from './matchThreshold';

// 'read' is the Already Read button, named for the shelf it files the book on.
export const DECK_ACTIONS = ['save', 'buy', 'read'];
// The most the database function accepts. The deck's page is 20.
export const MAX_RESULTS = 100;

// match_books rows -> what the function takes, in the same order. A row with no
// usable book id is dropped (it could not be stored). A missing distance gives a
// null similarity, never 0: unknown is not zero (matchThreshold.js).
export function buildResultsPayload(rows) {
  const out = [];
  for (const r of Array.isArray(rows) ? rows : []) {
    const bookId = Number(r?.book_id);
    if (!Number.isInteger(bookId) || bookId <= 0) continue;
    const sim = similarityFromDistance(r.distance);
    out.push({ book_id: bookId, similarity: Number.isFinite(sim) ? sim : null });
    if (out.length === MAX_RESULTS) break;
  }
  return out;
}

// A network failure can throw rather than return { error }. Neither helper may.
async function call(client, fn, args) {
  try {
    return await client.rpc(fn, args);
  } catch (e) {
    return { data: null, error: { message: e?.message || 'the request failed' } };
  }
}

// Was a filter on for this run? `facets` is exactly what match_books was sent.
// exclude_tags never counts: those are the reader's saved content-warning
// exclusions, which apply to every run, so a page with only those is the
// craving unfiltered. Any other facet with something in it counts. A page
// recorded as filtered reads lower than an unfiltered one, so whoever tunes
// the threshold on these similarities (ticket 52) reads filtered = false only.
export function isFilteredRun(facets) {
  if (!facets || typeof facets !== 'object') return false;
  return Object.entries(facets).some(([key, value]) => {
    if (key === 'exclude_tags') return false;
    if (Array.isArray(value)) return value.length > 0;
    if (typeof value === 'string') return value.trim() !== '';
    return value !== null && value !== undefined;
  });
}

// `filtered` has to be a real true or false: the database refuses a page that
// does not say, rather than recording one of unknown filter state.
export async function recordCravingResults(cravingId, rows, filtered, client = supabase) {
  if (cravingId == null) return { ok: false, status: 'no_craving', error: null };
  if (typeof filtered !== 'boolean') return { ok: false, status: 'no_filter_flag', error: null };
  const payload = buildResultsPayload(rows);
  if (payload.length === 0) return { ok: true, status: 'nothing_to_record', error: null };
  const { data, error } = await call(client, 'record_craving_results', { p_craving_id: cravingId, p_results: payload, p_filtered: filtered });
  if (error) return { ok: false, status: 'error', error: error.message };
  // Only 'recorded' is a write that happened. 'already_recorded' means this
  // craving's page was written before: the caller runs this once per craving,
  // so seeing it is worth a look.
  return { ok: data === 'recorded', status: data, error: null };
}

export async function recordCravingAction(cravingId, bookId, action, client = supabase) {
  if (cravingId == null) return { ok: false, status: 'no_craving', error: null };
  const id = Number(bookId);
  if (!Number.isInteger(id) || id <= 0) return { ok: false, status: 'no_book', error: null };
  if (!DECK_ACTIONS.includes(action)) return { ok: false, status: 'unknown_action', error: null };
  const { data, error } = await call(client, 'record_craving_action', { p_craving_id: cravingId, p_book_id: id, p_action: action });
  if (error) return { ok: false, status: 'error', error: error.message };
  // 'already_recorded' is a double tap on the same book and action: fine.
  // 'too_old' is a guest craving more than a day old (a deck left open): the
  // database wrote nothing, and nothing is wrong, so it is not reported.
  return { ok: data === 'recorded' || data === 'already_recorded' || data === 'too_old', status: data, error: null };
}
